import { createRootRoute, createRoute, createRouter, Outlet, redirect } from "@tanstack/react-router";
import { authClient } from "./lib/auth";
import { Home } from "./pages/Home";
import { SignInPage } from "./pages/SignIn";
import { ConfirmEmailPage, SignInLinkPage } from "./pages/EmailLinks";
import { PublicClubPage } from "./components/PublicClub";
import { api } from "./lib/api";
import { Clubs } from "./pages/Clubs";
import { Join } from "./pages/Join";
import { MePage } from "./pages/Me";
import { SettingsPage } from "./pages/Settings";
import { SharedCardPage } from "./pages/SharedCard";
import { WelcomePage, useWelcomeGate, type WelcomeSearch } from "./pages/Welcome";
import { ProfileProvider } from "./components/Profile";
import { rememberSignedIn, trackReturns } from "./lib/returnTo";
import { ClubLayout } from "./pages/club/ClubLayout";
import { NowTab } from "./pages/club/NowTab";
import { TalkTab } from "./pages/club/TalkTab";
import { ShelfTab } from "./pages/club/ShelfTab";
import { ClubTab } from "./pages/club/ClubTab";
import { ClubSettingsPage } from "./pages/club/ClubSettingsPage";
import { MembersPage } from "./pages/club/MembersPage";
import { NewClub } from "./pages/NewClub";
import { Directory, type DirSearch } from "./pages/Directory";
import { SuggestionsPage } from "./pages/club/SuggestionsPage";
import { RecapPage } from "./pages/club/RecapPage";
import { MeetingPageView } from "./pages/club/MeetingPage";
import { NotesPage } from "./pages/club/NotesPage";
import { DevLogin } from "./pages/DevLogin";
import { InboxPage } from "./pages/Inbox";
import { PushAskHost } from "./components/PushAsk";
import { useNotificationBoot } from "./lib/push";
import { UpdateBanner } from "./components/UpdateBanner";

// Code-based route tree. Screens per PLAN.md §2 "Screens".
// Someone's library card can open over any screen (ProfileProvider).
const rootRoute = createRootRoute({ component: () => <ProfileProvider><Outlet /><UpdateBanner /></ProfileProvider> });

// Signed-in gate for everything under it. Sends to the sign-in page with
// `next` so sign-in returns the viewer where they were going. One exception:
// a public club's link, signed out, shows that club's page (Authed below).
const NOT_A_CLUB = new Set(["new", "browse"]);
async function requireSession({ location }: { location: { href: string; pathname: string } }) {
  const { data } = await authClient.getSession();
  rememberSignedIn(Boolean(data));
  if (data) return { user: data.user, publicClub: null };
  const slug = /^\/clubs\/([^/]+)/.exec(location.pathname)?.[1];
  if (slug && !NOT_A_CLUB.has(slug) && (await api.publicClub(slug).then(() => true, () => false))) return { user: null, publicClub: slug };
  throw redirect({ to: "/signin", search: { next: location.href } });
}

// "/" and "/signin" are also prebuilt as static pages (prerender.tsx); these routes serve them inside the app.
const landingRoute = createRoute({
  getParentRoute: () => rootRoute, path: "/", component: Home,
  validateSearch: (s: Record<string, unknown>) => ({ next: typeof s.next === "string" ? s.next : undefined }),
});
const signInRoute = createRoute({
  getParentRoute: () => rootRoute, path: "/signin", component: SignInPage,
  validateSearch: (s: Record<string, unknown>): { next?: string } => (typeof s.next === "string" ? { next: s.next } : {}),
});
// Where the buttons in our emails land; signed out is fine (PLAN.md §Email addresses and email sign-in).
const signInLinkRoute = createRoute({ getParentRoute: () => rootRoute, path: "/signin/code", component: SignInLinkPage });
const confirmEmailRoute = createRoute({ getParentRoute: () => rootRoute, path: "/confirm-email", component: ConfirmEmailPage });
const joinRoute = createRoute({ getParentRoute: () => rootRoute, path: "/join/$code", component: Join });

const devLoginRoute = createRoute({ getParentRoute: () => rootRoute, path: "/dev-login", component: DevLogin });

// The first sign-in flow runs before anything else, once (useWelcomeGate).
function Authed() {
  const { publicClub } = authedRoute.useRouteContext();
  return publicClub ? <PublicClubPage clubId={publicClub} signedOut /> : <SignedIn />;
}
function SignedIn() { useWelcomeGate(); useNotificationBoot(); return <><Outlet /><PushAskHost /></>; }
const authedRoute = createRoute({ getParentRoute: () => rootRoute, id: "authed", beforeLoad: requireSession, component: Authed });
// A library card's share link: signed out is fine.
const sharedCardRoute = createRoute({ getParentRoute: () => rootRoute, path: "/p/$slug", component: SharedCardPage });
const clubsRoute = createRoute({
  getParentRoute: () => authedRoute, path: "/clubs", component: Clubs,
  validateSearch: (s: Record<string, unknown>): { all?: boolean } => (s.all ? { all: true } : {}),
});
const browseRoute = createRoute({
  getParentRoute: () => authedRoute, path: "/clubs/browse", component: Directory,
  validateSearch: (s: Record<string, unknown>): DirSearch => ({
    ...(typeof s.q === "string" && s.q ? { q: s.q } : {}),
    ...(Number.isInteger(Number(s.book)) && Number(s.book) > 0 ? { book: Number(s.book), bookTitle: typeof s.bookTitle === "string" ? s.bookTitle : undefined } : {}),
    ...(Array.isArray(s.genre) ? { genre: s.genre.filter((g): g is string => typeof g === "string") } : typeof s.genre === "string" ? { genre: [s.genre] } : {}),
    ...(["relaxed", "steady", "fast"].includes(s.pace as string) ? { pace: s.pace as string } : {}),
    ...(["small", "medium", "large"].includes(s.size as string) ? { size: s.size as string } : {}),
    ...(["open", "request"].includes(s.join as string) ? { join: s.join as string } : {}),
    ...(["newest", "members"].includes(s.sort as string) ? { sort: s.sort as string } : {}),
  }),
});
const newClubRoute = createRoute({
  getParentRoute: () => authedRoute, path: "/clubs/new", component: NewClub,
  validateSearch: (s: Record<string, unknown>): { step?: number } => (Number.isInteger(Number(s.step)) && Number(s.step) > 0 ? { step: Number(s.step) } : {}),
});
const meRoute = createRoute({ getParentRoute: () => authedRoute, path: "/me", component: MePage });
const inboxRoute = createRoute({ getParentRoute: () => authedRoute, path: "/inbox", component: InboxPage });
const welcomeRoute = createRoute({
  getParentRoute: () => authedRoute, path: "/welcome", component: WelcomePage,
  validateSearch: (s: Record<string, unknown>): WelcomeSearch => ({
    ...(Number.isInteger(Number(s.step)) && Number(s.step) > 0 ? { step: Number(s.step) } : {}),
    ...(typeof s.next === "string" ? { next: s.next } : {}), ...(typeof s.hardcover === "string" ? { hardcover: s.hardcover } : {}),
  }),
});
const settingsRoute = createRoute({
  getParentRoute: () => authedRoute, path: "/me/settings", component: SettingsPage,
  // Optional-only search keys, so plain <Link to="/me/settings"> needs no search prop.
  validateSearch: (s: Record<string, unknown>): { hardcover?: string; linked?: string } =>
    ({ ...(typeof s.hardcover === "string" ? { hardcover: s.hardcover } : {}), ...(typeof s.linked === "string" ? { linked: s.linked } : {}) }),
});
const clubRoute = createRoute({ getParentRoute: () => authedRoute, path: "/clubs/$clubId", component: ClubLayout });
const nowRoute = createRoute({ getParentRoute: () => clubRoute, path: "/", component: NowTab });
const talkRoute = createRoute({
  getParentRoute: () => clubRoute, path: "/talk", component: TalkTab,
  // ?at=<comment>: open the stream scrolled to that message (the unsealing moment's first opened one).
  validateSearch: (s: Record<string, unknown>): { thread?: string; book?: string; at?: string } => ({
    ...(typeof s.thread === "string" ? { thread: s.thread } : {}),
    ...(typeof s.book === "string" ? { book: s.book } : {}),
    ...(typeof s.at === "string" ? { at: s.at } : {}),
  }),
});
const shelfRoute = createRoute({
  getParentRoute: () => clubRoute, path: "/shelf", component: ShelfTab,
  // The open book panel, so a trip to the recap and back lands on the same book.
  validateSearch: (s: Record<string, unknown>): { book?: string } => (typeof s.book === "string" ? { book: s.book } : {}),
});
const suggestionsRoute = createRoute({
  getParentRoute: () => clubRoute, path: "/suggestions", component: SuggestionsPage,
  validateSearch: (s: Record<string, unknown>): { book?: string; mine?: boolean; add?: boolean } => ({
    ...(typeof s.book === "string" ? { book: s.book } : {}), ...(s.mine ? { mine: true } : {}), ...(s.add ? { add: true } : {}),
  }),
});
const clubTabRoute = createRoute({ getParentRoute: () => clubRoute, path: "/club", component: ClubTab,
  // ?post=<id>: a noticeboard thread, open inline on desktop or as its own screen on phones.
  validateSearch: (s: Record<string, unknown>): { invite?: boolean; post?: string } => ({ ...(s.invite ? { invite: true } : {}), ...(typeof s.post === "string" ? { post: s.post } : {}) }) });
const clubSettingsRoute = createRoute({ getParentRoute: () => clubRoute, path: "/club/settings", component: ClubSettingsPage });
const membersRoute = createRoute({ getParentRoute: () => clubRoute, path: "/club/members", component: MembersPage });
const recapRoute = createRoute({ getParentRoute: () => clubRoute, path: "/recap/$bookId", component: RecapPage });
const notesRoute = createRoute({ getParentRoute: () => clubRoute, path: "/notes/$bookId", component: NotesPage });
const meetingRoute = createRoute({
  getParentRoute: () => clubRoute, path: "/meeting", component: MeetingPageView,
  validateSearch: (s: Record<string, unknown>): { book?: string } => (typeof s.book === "string" ? { book: s.book } : {}),
});

export const router = createRouter({
  routeTree: rootRoute.addChildren([
    landingRoute, signInRoute, signInLinkRoute, confirmEmailRoute, joinRoute, sharedCardRoute, ...(import.meta.env.DEV ? [devLoginRoute] : []),
    authedRoute.addChildren([clubsRoute, newClubRoute, browseRoute, meRoute, inboxRoute, settingsRoute, welcomeRoute, clubRoute.addChildren([nowRoute, talkRoute, shelfRoute, clubTabRoute,
    clubSettingsRoute, membersRoute, recapRoute, meetingRoute, notesRoute, suggestionsRoute])]),
  ]),
  defaultPreload: "intent",
});

trackReturns(router);

declare module "@tanstack/react-router" {
  interface Register { router: typeof router; }
}
